import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../helpers'
import {
    GuestCard,
    GuestCardQuestionType,
    GuestCardRequiredFields,
} from '../../models/Outreach/types'

export const useGuestCards = () => {
    const queryClient = useQueryClient()

    const {
        data: guestCards,
        isLoading: isLoadingGuestCards,
        error: errorGuestCards,
    } = useQuery<AxiosResponse<GuestCard[]>, Error>({
        queryKey: ['guest-cards'],
        queryFn: () => axiosInstance.get('/outreach/guest-cards/'),
        enabled: true,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const { data: formDefaults } = useQuery<
        AxiosResponse<{
            default_move_in_date: string | null
            default_move_out_date: string | null
        }>,
        Error
    >({
        queryKey: ['guest-card-form-defaults'],
        queryFn: () =>
            axiosInstance.get('/outreach/guest-cards/form-defaults/'),
        enabled: true,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const createGuestCard = useMutation({
        mutationFn: (request: CreateGuestCardRequest) =>
            axiosInstance.post('/outreach/guest-cards/', request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['guest-cards'],
            })
            toast.success('Guest card created successfully')
        },
        onError: (error) => {
            toast.error('Failed to create guest card')
        },
    })

    const updateGuestCard = useMutation({
        mutationFn: (request: UpdateGuestCardRequest) =>
            axiosInstance.patch(
                `/outreach/guest-cards/${request.id}/`,
                request,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['guest-cards'],
            })
            toast.success('Guest card updated successfully')
        },
        onError: (error) => {
            toast.error('Failed to update guest card')
        },
    })

    return {
        guestCards: guestCards?.data,
        isLoadingGuestCards,
        errorGuestCards,
        defaultMoveInDate: formDefaults?.data?.default_move_in_date ?? null,
        defaultMoveOutDate: formDefaults?.data?.default_move_out_date ?? null,
        createGuestCard,
        updateGuestCard,
    }
}

export interface ExistingQuestionAttachment {
    id: number
    order: number
    required: boolean
}

export interface InlineQuestionCreation {
    question: string
    question_type: GuestCardQuestionType
    order: number
    required: boolean
    options?: string[]
}

export type QuestionInput =
    | string
    | ExistingQuestionAttachment
    | InlineQuestionCreation

export interface CreateGuestCardRequest {
    card_name: string
    header: string
    body: string
    name: boolean
    email: boolean
    phone: boolean
    preferred_unit_config: boolean
    preferred_premiums: boolean
    preferred_rent_cost: boolean
    preferred_move_in_date: boolean
    preferred_move_out_date: boolean
    preferred_roommates: boolean
    school_attendance: boolean
    additional_requests: boolean
    required_fields?: GuestCardRequiredFields
    questions: QuestionInput[]
}

export interface UpdateGuestCardRequest {
    id: number
    card_name?: string
    header?: string
    body?: string
    name?: boolean
    email?: boolean
    phone?: boolean
    preferred_unit_config?: boolean
    preferred_premiums?: boolean
    preferred_rent_cost?: boolean
    preferred_move_in_date?: boolean
    preferred_move_out_date?: boolean
    preferred_roommates?: boolean
    school_attendance?: boolean
    additional_requests?: boolean
    required_fields?: GuestCardRequiredFields
    questions?: QuestionInput[]
}
